import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Input,
  Chip,
  Tooltip,
  Badge,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { ExporterReceiver } from 'common/src/utils/types';
import useUserStore from '../../../../common/src/stores/useUserStore';
import { AddClientModal } from './AddClientModal';
import { ClientUsersModal } from './ClientUsersModal';
import { supabase } from 'common/src/supabase';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import useReceiverStore from '../../store/useReceiverStore';
import { LoadingState } from '../ui';

type AutomatedReceiverRow = {
  name: string | null;
  alternative_names: string[] | null;
};

export const ExporterReceiversSection = () => {
  const { t } = useTranslation('settings');
  const { userProfile } = useUserStore();
  // Map of normalized alias/name -> canonical receiver name
  const [aliasToCanonical, setAliasToCanonical] = useState<
    Record<string, string>
  >({});
  const [editingReceiver, setEditingReceiver] =
    useState<ExporterReceiver | null>(null);
  const [usersReceiver, setUsersReceiver] = useState<ExporterReceiver | null>(
    null
  );
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();
  const {
    isOpen: isAddOpen,
    onOpen: onAddOpen,
    onClose: onAddClose,
  } = useDisclosure();
  const {
    isOpen: isUsersOpen,
    onOpen: onUsersOpen,
    onClose: onUsersClose,
  } = useDisclosure();
  const {
    exporterReceivers,
    exporterReceiversLoading,
    fetchExporterReceivers,
    updateExporterReceiverName,
    deleteExporterReceiver,
  } = useReceiverStore();

  const fetchAutomatedReceivers = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('receivers')
        .select('name, alternative_names');
      if (error) throw error;

      const receiverRows = (data || []) as AutomatedReceiverRow[];

      // Build alias->canonical map with normalization
      const aliasMap: Record<string, string> = {};
      receiverRows.forEach((r) => {
        const canonical = String(r.name || '').trim();
        if (!canonical) return;
        const norm = canonical.toUpperCase();
        aliasMap[norm] = canonical;
        const alts: string[] = Array.isArray(r.alternative_names)
          ? r.alternative_names
          : [];
        alts.forEach((alt: string) => {
          const n = String(alt || '').trim();
          if (!n) return;
          aliasMap[n.toUpperCase()] = canonical;
        });
      });
      setAliasToCanonical(aliasMap);
    } catch (error) {
      toast.error(t('Error al cargar receptores automatizados.'));
      console.error(error);
    }
  }, [t]);

  const isAutomatedName = useMemo(() => {
    return (name: string) =>
      Boolean(aliasToCanonical[name.trim().toUpperCase()]);
  }, [aliasToCanonical]);

  useEffect(() => {
    if (userProfile?.exporter_id) {
      fetchExporterReceivers(userProfile.exporter_id, true).catch(() =>
        toast.error(t('Error al cargar los clientes.'))
      );
    }
    fetchAutomatedReceivers();
  }, [
    userProfile?.exporter_id,
    fetchExporterReceivers,
    fetchAutomatedReceivers,
    t,
  ]);

  const handleUpdate = async () => {
    if (!editingReceiver) return;
    try {
      await updateExporterReceiverName(
        editingReceiver.id,
        editingReceiver.name
      );
      onEditClose();
      toast.success(t('Cliente actualizado con éxito.'));
    } catch (error) {
      toast.error(t('Error al actualizar el cliente.'));
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteExporterReceiver(id);
      toast.success(t('Cliente eliminado con éxito.'));
    } catch (error) {
      toast.error(t('Error al eliminar el cliente.'));
    }
  };

  const startEditing = (receiver: ExporterReceiver) => {
    setEditingReceiver(receiver);
    onEditOpen();
  };

  const openUsers = (receiver: ExporterReceiver) => {
    setUsersReceiver(receiver);
    onUsersOpen();
  };

  const updateReceiverUserCount = useCallback(
    (receiverId: number, count: number) => {
      useReceiverStore.setState((state) => ({
        exporterReceivers: state.exporterReceivers.map((receiver) =>
          receiver.id === receiverId
            ? {
                ...receiver,
                receiver_access_user_count: count,
              }
            : receiver
        ),
      }));
    },
    []
  );

  const closeUsers = () => {
    onUsersClose();
    setUsersReceiver(null);
    if (userProfile?.exporter_id) {
      fetchExporterReceivers(userProfile.exporter_id, true).catch(() => null);
    }
  };

  const invitedReceiverUsersCount = useMemo(
    () =>
      exporterReceivers.reduce(
        (total, receiver) =>
          total +
          (receiver.receiver_access_user_count ??
            receiver.receiver_access_users?.length ??
            0),
        0
      ),
    [exporterReceivers]
  );

  return (
    <div>
      <div className='flex flex-col gap-3 mb-4'>
        <div className='flex flex-col gap-3 md:flex-row md:items-center md:justify-between'>
          <div>
            <p className='text-lg font-bold'>{t('Clientes')}</p>
            <p className='text-sm text-default-400'>
              {t(
                'Agrega los clientes con los que trabajas, para poder automatizar sus formatos de liquidaciones.'
              )}
            </p>
          </div>
          <Button size='sm' color='primary' onPress={onAddOpen}>
            <Icon icon='tabler:plus' />
            {t('Nuevo Cliente')}
          </Button>
        </div>

        {/* <div className='rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 dark:border-primary-900/60 dark:bg-primary-950/30'>
          <div className='flex flex-col gap-3 md:flex-row md:items-center md:justify-between'>
            <div className='flex min-w-0 items-start gap-3'>
              <div className='mt-0.5 flex h-9 w-9 flex-none items-center justify-center rounded-md bg-primary-100 text-primary-600 dark:bg-primary-900/50 dark:text-primary-300'>
                <Icon icon='tabler:users' />
              </div>
              <div className='min-w-0'>
                <p className='text-sm font-semibold text-primary-700 dark:text-primary-200'>
                  {t('Acceso para usuarios receptores')}
                </p>
                <p className='mt-1 text-sm text-default-600'>
                  {t(
                    'Invita a tus clientes para que revisen sus compromisos comerciales y estado de sus embarques, directamente desde la plataforma.'
                  )}
                </p>
              </div>
            </div>
            <div className='flex flex-none items-center gap-3 rounded-md bg-background/80 px-3 py-2 shadow-sm'>
              <div>
                <p className='text-xl font-semibold leading-none'>
                  {invitedReceiverUsersCount}
                </p>
                <p className='mt-1 text-xs text-default-500'>
                  {t('usuarios invitados')}
                </p>
              </div>
              <div className='h-8 border-l border-divider' />
              <p className='max-w-[150px] text-xs text-default-500'>
                {t('Usa el botón Usuarios en cada cliente para invitar.')}
              </p>
            </div>
          </div>
        </div> */}
      </div>

      {exporterReceiversLoading ? (
        <LoadingState />
      ) : (
        <Table aria-label={t('Tabla de Clientes')}>
          <TableHeader>
            <TableColumn>{t('NOMBRE')}</TableColumn>
            <TableColumn>{t('ESTADO ROBOT LIQUIDACIONES')}</TableColumn>
            <TableColumn>{t('ACCIONES')}</TableColumn>
          </TableHeader>
          <TableBody items={exporterReceivers}>
            {(item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <div className='flex items-center gap-2'>
                    <span>{item.name}</span>
                  </div>
                </TableCell>
                <TableCell>
                  {isAutomatedName(item.name) ? (
                    <Tooltip
                      content={t('Plantilla de liquidación automatizada')}
                    >
                      <Chip color='success' variant='flat'>
                        {t('Automatizado')}
                      </Chip>
                    </Tooltip>
                  ) : (
                    <Tooltip content={t('Subida manual de liquidación')}>
                      <Chip color='default' variant='flat'>
                        {t('Manual')}
                      </Chip>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  <div className='flex gap-2'>
                    {/* <Badge
                      content={
                        item.receiver_access_user_count ??
                        item.receiver_access_users?.length ??
                        0
                      }
                      color='primary'
                      size='sm'
                    >
                      <Button
                        size='sm'
                        variant='flat'
                        onPress={() => openUsers(item)}
                        startContent={<Icon icon='tabler:users' />}
                      >
                        {t('Usuarios')}
                      </Button>
                    </Badge> */}
                    <Tooltip content={t('Editar')}>
                      <Button
                        isIconOnly
                        size='sm'
                        variant='light'
                        aria-label={t('Editar')}
                        onPress={() => startEditing(item)}
                      >
                        <Icon icon='tabler:edit' />
                      </Button>
                    </Tooltip>
                    <Tooltip content={t('Eliminar')}>
                      <Button
                        isIconOnly
                        size='sm'
                        variant='light'
                        aria-label={t('Eliminar')}
                        onPress={() => handleDelete(item.id)}
                      >
                        <Icon icon='tabler:trash' />
                      </Button>
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <Modal isOpen={isEditOpen} onClose={onEditClose}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>{t('Editar Cliente')}</ModalHeader>
              <ModalBody>
                <Input
                  label={t('Nombre del cliente')}
                  value={editingReceiver?.name || ''}
                  onChange={(e) => {
                    if (editingReceiver) {
                      setEditingReceiver({
                        ...editingReceiver,
                        name: e.target.value,
                      });
                    }
                  }}
                />
              </ModalBody>
              <ModalFooter>
                <Button variant='light' onPress={onClose}>
                  {t('Cancelar')}
                </Button>
                <Button color='primary' onPress={handleUpdate}>
                  {t('Guardar')}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {userProfile?.exporter_id && (
        <AddClientModal
          isOpen={isAddOpen}
          onClose={onAddClose}
          exporterId={userProfile.exporter_id}
          existingClientNames={exporterReceivers.map(
            (receiver) => receiver.name
          )}
        />
      )}
      <ClientUsersModal
        isOpen={isUsersOpen}
        onClose={closeUsers}
        receiver={usersReceiver}
        onUsersLoaded={updateReceiverUserCount}
      />
    </div>
  );
};
